import { CompanyType, isBondCompany } from '../input/companies/types';
import { ModelEntityType } from '../input/types';
import { ModelEntitySearchResponseItem } from './types';

/**
 * Deterministic match-confidence classification for company search hits.
 *
 * Pure functions over search-response items — no I/O, no LLM, no DB round-trip —
 * so they are cheap to run per query and safe to import from both backend and
 * frontend code. The classification lets a caller decide when the top hit is
 * strong enough to auto-select versus when it should surface a "verify this"
 * signal (or ask the user to disambiguate) instead of silently picking the top.
 *
 * The logic originated in the chat company-search tool
 * (`src/services/main/upserve/modules/mcp/tools/search.ts`); see PLT-4455 /
 * PLT-4525 / PLT-4551 for the individual tie-breakers. It lives here so the
 * self-serve portfolio picker can reuse the same gating without depending on
 * MCP code. (De-duplicating the chat's own copy onto this module is tracked
 * separately in PLT-5579.)
 */

// Match-confidence thresholds. When the top score is weak or two top results
// are close, the caller should treat the top hit as uncertain rather than
// silently picking it (the "wrong company picked" failure mode of e.g.
// PLT-4378). Initial values come from Milja's PLT-4455 spec.
export const LOW_CONFIDENCE_SCORE_THRESHOLD = 200;
export const AMBIGUOUS_RELATIVE_GAP = 0.1;

/**
 * Popularity tier used as a tie-breaker when ES scores cluster within
 * `AMBIGUOUS_RELATIVE_GAP`. Higher = more likely the parent / megacap a user
 * means by a bare brand name.
 *
 * Why coarse tiers (instead of e.g. raw revenue): every signal here is
 * already on the search hit, so no extra DB / Demandbase round-trip is
 * needed. PLT-4525 introduced this to fix the "Apple" regression where
 * Apple Inc., Apple (Japan), and Apple Finance (India) clustered within
 * 10% on raw ES score and forced the user to disambiguate.
 *
 * - 3 — Parent listed entity: PUBLIC + CompanyType.COMPANY (or unset
 *   companyType, which we treat as the default "regular company"). This is
 *   the typical megacap shape (Apple Inc., Nordea, BASF, …).
 * - 2 — Listed but a non-COMPANY type (e.g. BUSINESS_UNIT). Listed enough
 *   to outrank purely private / niche entities, but not the parent shape.
 *   Note: bond / SPV proxies are demoted to tier 1 even when listed —
 *   they are never the entity a bare brand name means.
 * - 1 — Everything else: PRIVATE companies, UNKNOWN listing, bond / SPV
 *   proxies, etc.
 * - 0 — Non-company entities (groups, portfolios). Kept as a defensive
 *   floor so non-companies cannot win the tie-breaker.
 */
export type PopularityTier = 0 | 1 | 2 | 3;

export function popularityTier(
  item: Pick<ModelEntitySearchResponseItem, 'type' | 'companyType' | 'listingStatus'>,
): PopularityTier {
  if (item.type !== ModelEntityType.COMPANY) return 0;
  // Bond / SPV proxies share the parent's brand name and can score very
  // close to it. Demote here so they never beat the parent on the
  // popularity signal.
  if (isBondCompany(item.companyType)) return 1;
  if (item.listingStatus === 'PUBLIC') {
    // CompanyType is sometimes unset on older records — treat that as
    // the default "regular company" rather than penalising it.
    if (item.companyType === undefined || item.companyType === CompanyType.COMPANY) return 3;
    return 2;
  }
  return 1;
}

export type MatchQuality = 'confident' | 'ambiguous' | 'low-confidence';

/**
 * Common legal-suffix tokens stripped from company names before computing the
 * name-match tier (PLT-4551). Lowercased and punctuation-free — `tokeniseName`
 * folds accents and drops `.` / `,` / `/` / `&` before comparing against this
 * set, so "S.A." → "sa", "A/S" → "as", etc. Kept as a positive list so a brand
 * token that happens to sit at the end of a name (e.g. "Group" in "Group")
 * cannot be accidentally stripped.
 */
const LEGAL_SUFFIX_TOKENS = new Set<string>([
  // Anglo / generic
  'inc',
  'incorporated',
  'corp',
  'corporation',
  'co',
  'company',
  'ltd',
  'limited',
  'llc',
  'lp',
  'llp',
  'plc',
  'group',
  'groupe',
  'holding',
  'holdings',
  // Continental Europe
  'ag',
  'sa',
  'sas',
  'sarl',
  'se',
  'spa',
  'srl',
  'gmbh',
  'kg',
  'ohg',
  'nv',
  'bv',
  'sl',
  // Nordic
  'oyj',
  'oy',
  'ab',
  'as',
  'asa',
  'aps',
  // Asia / Pacific
  'kk',
  'pte',
  'pty',
]);

/**
 * Atomic Latin letters that NFD does NOT decompose because they're not
 * "Latin letter + combining mark" but their own code points. Without
 * pre-folding these, "Ørsted" stays "ørsted" through `foldAccents` and a
 * query of "orsted" misses a canonical-equal match against it.
 *
 * Keys are the lowercase forms because the only call site lowercases its
 * input before calling `foldAccents`.
 */
const ATOMIC_LATIN_FOLDS: ReadonlyMap<string, string> = new Map([
  ['ø', 'o'],
  ['æ', 'ae'],
  ['œ', 'oe'],
  ['ß', 'ss'],
  ['ð', 'd'],
  ['þ', 'th'],
  ['đ', 'd'],
  ['ł', 'l'],
]);
const ATOMIC_LATIN_FOLD_RE = new RegExp(`[${[...ATOMIC_LATIN_FOLDS.keys()].join('')}]`, 'g');

/**
 * Folds combining marks (accents) so "Nestlé" tokenises the same way as its
 * unaccented form, AND folds atomic Latin letters (Ø, æ, œ, ß, …) to their
 * nearest ASCII equivalents so a plain-ASCII query like "orsted" matches
 * "Ørsted A/S". NFD alone does not handle the atomic letters because they
 * aren't "base + combining mark"; we pre-fold them via `ATOMIC_LATIN_FOLDS`,
 * then strip combining marks for the rest.
 */
export function foldAccents(s: string): string {
  return s
    .replace(ATOMIC_LATIN_FOLD_RE, c => ATOMIC_LATIN_FOLDS.get(c) ?? c)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .normalize('NFC');
}

function tokeniseName(s: string): string[] {
  // `.` and `/` sit *inside* compound abbreviations ("A/S", "S.A.", "Inc."),
  // so squash them away rather than splitting on them — otherwise "Ørsted A/S"
  // tokenises to ['orsted','a','s'] and `canonicalNameTokens` only pops the
  // trailing 's', leaving 'orsted a' instead of 'orsted'. The Danish "A/S"
  // legal-suffix entry only exists as the joined token 'as' in
  // `LEGAL_SUFFIX_TOKENS`. `,`, `&`, `(`, `)` are genuine word boundaries.
  return foldAccents(s.toLowerCase())
    .replace(/[./]/g, '')
    .replace(/[,&()]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 0);
}

/**
 * Returns the canonical (legal-suffix-stripped) tokens for a name. Stops
 * stripping when only one token remains so a company actually called "AG"
 * (or whose brand is "Group", "Plc", …) does not collapse to the empty string.
 */
function canonicalNameTokens(name: string): string[] {
  const tokens = tokeniseName(name);
  while (tokens.length > 1 && LEGAL_SUFFIX_TOKENS.has(tokens[tokens.length - 1])) {
    tokens.pop();
  }
  return tokens;
}

function canonicalName(name: string): string {
  return canonicalNameTokens(name).join(' ');
}

/**
 * Name-match tier used as a tie-breaker AFTER popularity (PLT-4551). Higher =
 * the hit is a closer textual match for the user query than its near-tied
 * peers. Values:
 *
 * - 3 — canonical name (suffix-stripped, lowercased, accent-folded) equals
 *   the canonical query. Examples: query "Siemens" matches "Siemens AG".
 * - 1 — canonical name STARTS WITH the canonical query but carries additional
 *   brand / qualifier tokens. Examples: query "Siemens" matches "Siemens
 *   Energy AG" (canonical: "siemens energy") at tier 1.
 * - 0 — neither equals nor prefixes.
 *
 * Tier 2 (prefix-with-only-legal-suffix-trailing) collapses into tier 3 here
 * because the canonical form already strips trailing legal suffixes from both
 * sides. Dominance comparisons elsewhere use strict less-than against the top
 * tier (see `computeMatchQuality`); with the current 0 / 1 / 3 set this is
 * fine, but if a real tier 2 is ever introduced, every `nameMatchTier(...) <
 * topNameTier` callsite must be audited.
 */
export type NameMatchTier = 0 | 1 | 3;

export function nameMatchTier(query: string, name: string): NameMatchTier {
  const queryCanon = canonicalName(query);
  if (queryCanon.length === 0) return 0;
  const itemCanon = canonicalName(name);
  if (itemCanon === queryCanon) return 3;
  // Prefix check at the token level so "Siemens" does not falsely match "Sie".
  const queryTokens = queryCanon.split(' ');
  const itemTokens = itemCanon.split(' ');
  if (itemTokens.length > queryTokens.length && queryTokens.every((t, i) => itemTokens[i] === t)) {
    return 1;
  }
  return 0;
}

/**
 * Tokens that turn up in company names and user queries but do not identify an
 * entity on their own: the corporate-form words already in
 * `LEGAL_SUFFIX_TOKENS`, plus the geography-flavoured filler a hallucinated
 * name reaches for ("… International", "… Global"). Both sides are stripped of
 * these before the absolute comparison in `topHitLooksUnrelated`, so a hit that
 * shares nothing with the query except the word "International" gets no credit
 * for it.
 *
 * Deliberately NOT folded into `LEGAL_SUFFIX_TOKENS`: that set drives
 * `nameMatchTier`, where "Siemens Global" and "Siemens" must stay different
 * names.
 */
const GENERIC_NAME_TOKENS = new Set<string>(['international', 'global', 'worldwide', 'the', 'of', 'and']);

/**
 * The tokens of a name or query with corporate-form and generic filler removed
 * — what is left is the part that actually names an entity.
 *
 * Unlike `canonicalNameTokens` this strips matching tokens from ANY position,
 * not just the tail: "Acme Imaginary Holdings International" has to lose its
 * middle "Holdings" for the comparison to be about "acme imaginary".
 */
export function distinctiveNameTokens(s: string): string[] {
  return tokeniseName(s).filter(t => !LEGAL_SUFFIX_TOKENS.has(t) && !GENERIC_NAME_TOKENS.has(t));
}

/** Shortest token that may match by being a prefix of the other ("jp" in "jpmorgan"). */
const PREFIX_MATCH_MIN_LENGTH = 2;
/** Shortest token that may match by sitting anywhere inside the other ("cola" in "coca-cola"). */
const SUBSTRING_MATCH_MIN_LENGTH = 4;
/** Shortest token that may match through a single typo ("bnak" for "bank"). */
const EDIT_MATCH_MIN_LENGTH = 4;

/**
 * Whether two tokens differ by at most one insertion, deletion, substitution,
 * or transposition of adjacent characters (Damerau-Levenshtein distance <= 1,
 * decided directly rather than by building a distance matrix — the bound is 1,
 * so the three shapes are enumerable).
 */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (longer.length - shorter.length > 1) return false;

  if (longer.length === shorter.length) {
    // Substitution (one differing position) or transposition (two adjacent
    // positions holding each other's character).
    const differing: number[] = [];
    for (let i = 0; i < longer.length; i++) {
      if (longer[i] !== shorter[i]) {
        differing.push(i);
        if (differing.length > 2) return false;
      }
    }
    if (differing.length === 1) return true;
    if (differing.length !== 2) return false;
    const [first, second] = differing;
    return second === first + 1 && longer[first] === shorter[second] && longer[second] === shorter[first];
  }

  // One character inserted into `shorter`: walk both, allow a single skew.
  let skewed = false;
  for (let i = 0, j = 0; i < longer.length; i++, j++) {
    if (longer[i] === shorter[j]) continue;
    if (skewed) return false;
    skewed = true;
    // Consume the extra character on the longer side only.
    j--;
  }
  return true;
}

/**
 * Whether a query token and a name token plausibly refer to the same word.
 * Three tolerances, each earned by a real query shape:
 *
 * - prefix — "JP Morgan" against "JPMorgan Chase": the query splits a token the
 *   name joins. Short tokens are allowed here because a 2-letter prefix of a
 *   longer token is a much narrower claim than a 2-letter substring.
 * - substring — "Coca Cola" against "The Coca-Cola Company": `tokeniseName`
 *   keeps the hyphen, so "cola" only meets "coca-cola" inside it.
 * - one edit — "Deutsche Bnak": a typo in one token of a multi-token query must
 *   not read as a different company.
 */
function tokensCorrespond(a: string, b: string): boolean {
  if (a === b) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (shorter.length >= PREFIX_MATCH_MIN_LENGTH && longer.startsWith(shorter)) return true;
  if (shorter.length >= SUBSTRING_MATCH_MIN_LENGTH && longer.includes(shorter)) return true;
  if (shorter.length >= EDIT_MATCH_MIN_LENGTH && withinOneEdit(a, b)) return true;
  return false;
}

/**
 * Whether the top search hit cannot plausibly be the entity the query names.
 *
 * The gap this closes (PLT-6417 / PLT-6418): every signal in
 * `computeMatchQuality` compares the hits against EACH OTHER — score gap,
 * popularity, name-match tier relative to the runners-up, coverage. Nothing
 * asks whether the winner resembles what the user typed. So a fabricated
 * company name whose fuzzy top hit shares one token with it comes back
 * `confident`: `search("Acme Imaginary Holdings International")` returned
 * `confident` on "Acme Holdings Berhad", a Malaysian property developer.
 *
 * The test is two-sided on purpose. "The hit does not account for all of the
 * query" on its own would reject correct hits constantly, because real
 * companies are habitually typed under a longer former or colloquial name:
 * "Royal Dutch Shell" → Shell plc, "Tesla Motors" → Tesla, Inc. Those are
 * one-sided — the hit's name is a SUBSET of what was typed and introduces
 * nothing new. A fabricated name is two-sided: the hit both misses what was
 * typed ("imaginary") and brings tokens the query never mentioned ("berhad").
 *
 * So it fires only when all three hold:
 *
 * 1. the query names at least two distinctive tokens — a single-token query is
 *    what ES fuzziness is for, and there is nothing to be half-right about;
 * 2. the hit accounts for at most half of them;
 * 3. the hit carries at least one distinctive token of its own that the query
 *    never mentioned.
 *
 * Condition 2 is a half rather than "any miss" so that one unrecognised legal
 * form on the hit's side cannot trip it: `LEGAL_SUFFIX_TOKENS` does not list
 * every form on earth, and "Nordea Bank Finland" → "Nordea Bank Abp" has to
 * stay a match with 'abp' unaccounted for.
 *
 * Where the thresholds sit is a function of what firing costs, so do not move
 * one without checking the other. Firing means the chat asks the user to
 * confirm the hit by name, so a false positive costs a turn — which is what
 * makes it acceptable that at two distinctive tokens this cannot tell a
 * fabricated name from a real one typed with an extra qualifier ("Nordea
 * Finland" produces the same arithmetic as "Acme Imaginary Holdings"; both
 * fire). Wire it to anything that refuses instead of asking and that tolerance
 * stops being affordable.
 *
 * Only the chat/MCP `search` tool consumes this today; it deliberately does not
 * feed `computeMatchQuality`, because the frontend pickers that share that
 * function have a human looking at the candidate list and need no server-side
 * veto (PLT-6418's other lever — down-weighting generic tokens in the
 * Elasticsearch relevance query itself — is not implemented here).
 */
export function topHitLooksUnrelated(query: string, name: string): boolean {
  const queryTokens = distinctiveNameTokens(query);
  if (queryTokens.length < 2) return false;
  const nameTokens = distinctiveNameTokens(name);
  if (nameTokens.length === 0) return false;

  const accounted = queryTokens.filter(q => nameTokens.some(n => tokensCorrespond(q, n)));
  if (accounted.length * 2 > queryTokens.length) return false;

  return nameTokens.some(n => !queryTokens.some(q => tokensCorrespond(q, n)));
}

/**
 * Coverage signal for the dataset-coverage tie-breaker (PLT-4551). Higher =
 * richer dataset coverage on this entity. DMA outranks FE because DMA is the
 * higher-signal availability axis (it implies a current-release variant and
 * CSRD-result read access; FE may exist on internal releases only).
 */
function coverageScore(coverage: { dma?: boolean; fe?: boolean } | undefined): number {
  if (coverage === undefined) return 0;
  return (coverage.dma ? 2 : 0) + (coverage.fe ? 1 : 0);
}

/**
 * Classifies the confidence of the top company match.
 *
 * - `low-confidence` — top score is below LOW_CONFIDENCE_SCORE_THRESHOLD.
 * - `ambiguous` — at least two company results, the runner-up(s) score within
 *   AMBIGUOUS_RELATIVE_GAP of the top, AND none of the tie-breakers
 *   (popularity, name-match, dataset-coverage with same-canonical-name twin
 *   collapse) resolve the ambiguity.
 * - `confident` — otherwise. This includes the case where the top score is
 *   ahead by more than the gap, AND the case where the score gap alone would
 *   say "ambiguous" but a tie-breaker resolves the top hit.
 * - `undefined` — there are no company results to score-gate.
 *
 * `low-confidence` takes precedence over `ambiguous` when both apply: if the
 * best match is already weak, the score-gap signal is moot.
 *
 * Takes the full company items (not just scores) so the popularity /
 * name-match / coverage tie-breakers have access to listingStatus /
 * companyType / id / name. Pass `query` plus the optional `coverage` map (id →
 * {dma, fe}) to enable the name-match and coverage tie-breakers; without them
 * the function falls back to popularity-only behaviour. Items must be ordered
 * with the highest-scoring hit first.
 */
export type MatchQualityItem = Pick<
  ModelEntitySearchResponseItem,
  'score' | 'type' | 'companyType' | 'listingStatus'
> & {
  id?: string;
  name?: string;
};

export type MatchQualityOptions = {
  query?: string;
  coverage?: ReadonlyMap<string, { dma?: boolean; fe?: boolean }>;
};

export function computeMatchQuality(
  items: readonly MatchQualityItem[],
  options: MatchQualityOptions = {},
): MatchQuality | undefined {
  if (items.length === 0) return undefined;
  const top = items[0];
  const topScore = top.score;
  if (topScore === undefined) return undefined;
  if (topScore < LOW_CONFIDENCE_SCORE_THRESHOLD) return 'low-confidence';
  if (items.length < 2 || topScore <= 0) return 'confident';

  // Identify runner-ups whose score sits inside the "near-tied" band.
  const nearTied = items.slice(1).filter(r => {
    const s = r.score;
    if (s === undefined) return false;
    return (topScore - s) / topScore < AMBIGUOUS_RELATIVE_GAP;
  });
  if (nearTied.length === 0) return 'confident';

  // Popularity tie-breaker (PLT-4525): when the top hit is *strictly* more
  // popular than every near-tied runner-up, treat it as confident (Apple Inc.
  // vs Apple Japan / Apple Finance India). When two near-tied results share
  // the same popularity tier we fall through to the further tie-breakers.
  const topPopularity = popularityTier(top);
  if (nearTied.every(r => popularityTier(r) < topPopularity)) return 'confident';

  // Name-match tie-breaker (PLT-4551). Applied AFTER popularity and BEFORE
  // coverage so the cheaper / stronger signal wins first. Only fires when the
  // caller passed a `query`. Kills the Siemens shape: query "Siemens" with top
  // "Siemens AG" (tier 3) over "Siemens Energy AG" / "Siemens Healthineers AG"
  // (tier 1).
  if (options.query !== undefined) {
    const topNameTier = nameMatchTier(options.query, top.name ?? '');
    if (topNameTier > 0 && nearTied.every(r => nameMatchTier(options.query!, r.name ?? '') < topNameTier)) {
      return 'confident';
    }
  }

  // Dataset-coverage tie-breaker (PLT-4551), with same-canonical-name twin
  // collapse. Two profiles for what is effectively the same legal entity
  // (Ørsted A/S × 2) score near-identically and, until this rule, looked
  // ambiguous. Collapsing them by canonical name lets the richer-coverage
  // representative absorb its twins.
  if (options.coverage !== undefined) {
    const coverageMap = options.coverage;
    const topCoverage = top.id !== undefined ? coverageScore(coverageMap.get(top.id)) : 0;
    const topCanonical = canonicalName(top.name ?? '');
    // Twin-collapse asymmetry guard: only collapse twins whose coverage is
    // `<= topCoverage`. A twin with STRICTLY richer coverage must NOT be
    // silently absorbed into the top — keeping it in `remaining` makes the
    // dominance check fail and falls back to 'ambiguous'. See PR #7142 review.
    const remaining =
      topCanonical.length > 0
        ? nearTied.filter(r => {
            if (canonicalName(r.name ?? '') !== topCanonical) return true;
            const rCoverage = r.id !== undefined ? coverageScore(coverageMap.get(r.id)) : 0;
            return rCoverage > topCoverage;
          })
        : nearTied;
    if (remaining.length === 0) return 'confident';
    if (
      topCoverage > 0 &&
      remaining.every(r => (r.id !== undefined ? coverageScore(coverageMap.get(r.id)) : 0) < topCoverage)
    ) {
      return 'confident';
    }
  }

  return 'ambiguous';
}
